import React, { useEffect, useState } from 'react';
import {
  Box, Paper, Typography, CircularProgress, Table, TableHead, TableRow, TableCell, TableBody,
  Grid
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import api from '../../api';

/**
 * StatsEleves (jugeboxe)
 * - Props: classeId (string)
 * - Affiche podium (top3) + tableau complet, classement par wins desc, losses asc.
 * - Ajout de la colonne "Arbitres" (nombre de fois où l'élève a été juge).
 */
export default function StatsEleves({ classeId }) {
  const theme = useTheme();
  const custom = theme.palette.custom || {};

  const [loading, setLoading] = useState(true);
  const [elevesStats, setElevesStats] = useState([]); // { eleve, wins, losses, arbitres }
  const [error, setError] = useState('');

  useEffect(() => {
    if (!classeId) {
      setError('Aucune classe sélectionnée.');
      setLoading(false);
      return;
    }
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [classeId]);

  const tryFetchEleves = async (id) => {
    const attempts = [
      `/eleves?classe=${encodeURIComponent(id)}`,
      `/eleves?classeId=${encodeURIComponent(id)}`,
      `/classes/${encodeURIComponent(id)}/eleves`,
      `/students?classe=${encodeURIComponent(id)}`,
      `/students?classeId=${encodeURIComponent(id)}`
    ];
    for (const url of attempts) {
      try {
        const res = await api.get(url);
        if (Array.isArray(res.data)) return res.data;
        if (res.data && Array.isArray(res.data.data)) return res.data.data;
        if (res.data && Array.isArray(res.data.items)) return res.data.items;
      } catch (err) {
        // try next
      }
    }
    throw new Error('Aucun endpoint élèves trouvé pour cette classe');
  };

  const load = async () => {
    setLoading(true);
    setError('');
    setElevesStats([]);
    try {
      const eleves = await tryFetchEleves(classeId);

      const statsPromises = eleves.map(async (e) => {
        const id = e._id ?? e.id ?? e.id_eleve ?? '';
        if (!id) return { eleve: e, wins: 0, losses: 0, arbitres: 0 };
        try {
          const r = await api.get(`/stats/eleve/${encodeURIComponent(id)}`);
          const s = r.data || {};
          return { eleve: e, wins: s.wins ?? 0, losses: s.losses ?? 0, arbitres: s.arbitres ?? s.arbitresCount ?? 0 };
        } catch (err) {
          // fallback to inline statsByActivity if present
          const sba = e.statsByActivity || {};
          if (typeof sba === 'object' && Object.keys(sba).length > 0) {
            let wins = 0, losses = 0, arbitres = 0;
            Object.values(sba).forEach(act => {
              wins += Number(act.wins ?? 0);
              losses += Number(act.losses ?? 0);
              arbitres += Number(act.arbitres ?? act.arbitresCount ?? 0);
            });
            return { eleve: e, wins, losses, arbitres };
          }
          return { eleve: e, wins: 0, losses: 0, arbitres: 0 };
        }
      });

      const results = await Promise.all(statsPromises);

      results.sort((a, b) => {
        if ((b.wins ?? 0) !== (a.wins ?? 0)) return (b.wins ?? 0) - (a.wins ?? 0);
        if ((a.losses ?? 0) !== (b.losses ?? 0)) return (a.losses ?? 0) - (b.losses ?? 0);
        const an = (a.eleve.nom || a.eleve.lastname || '').toString();
        const bn = (b.eleve.nom || b.eleve.lastname || '').toString();
        if (an !== bn) return an.localeCompare(bn);
        return (a.eleve.prenom || '').toString().localeCompare((b.eleve.prenom || '').toString());
      });

      setElevesStats(results);
    } catch (err) {
      console.error('StatsEleves load error', err);
      setError(err.message || 'Erreur lors du chargement des statistiques');
    } finally {
      setLoading(false);
    }
  };

  if (loading) return <Box sx={{ mt: 6, textAlign: 'center' }}><CircularProgress /></Box>;
  if (error) return <Typography color="error" sx={{ m: 2 }}>{error}</Typography>;

  const top3 = elevesStats.slice(0, 3);

  return (
    <Box sx={{ mt: 3 }}>
      <Typography variant="h6" sx={{ mb: 2 }}>Classement — Podium</Typography>

      <Grid container spacing={2} alignItems="end">
        <Grid item xs={12} md={4}>
          <Paper sx={{ p: 2, textAlign: 'center', backgroundColor: custom.headerBg }}>
            <Typography variant="subtitle2" sx={{ color: 'text.secondary' }}>2ème</Typography>
            {top3[1] ? (
              <>
                <Typography variant="h6">{top3[1].eleve.prenom} {top3[1].eleve.nom}</Typography>
                <Typography>{top3[1].wins} victoires • {top3[1].losses} défaites • {top3[1].arbitres} arbitres</Typography>
              </>
            ) : <Typography sx={{ color: 'text.secondary' }}>—</Typography>}
          </Paper>
        </Grid>

        <Grid item xs={12} md={4}>
          <Paper sx={{ p: 3, textAlign: 'center', backgroundColor: custom.primaryBg, boxShadow: 3 }}>
            <Typography variant="subtitle2" sx={{ color: 'text.secondary' }}>1er</Typography>
            {top3[0] ? (
              <>
                <Typography variant="h5" sx={{ fontWeight: 700 }}>{top3[0].eleve.prenom} {top3[0].eleve.nom}</Typography>
                <Typography sx={{ mt: 1 }}>{top3[0].wins} victoires • {top3[0].losses} défaites • {top3[0].arbitres} arbitres</Typography>
              </>
            ) : <Typography sx={{ color: 'text.secondary' }}>—</Typography>}
          </Paper>
        </Grid>

        <Grid item xs={12} md={4}>
          <Paper sx={{ p: 2, textAlign: 'center', backgroundColor: custom.printBg }}>
            <Typography variant="subtitle2" sx={{ color: 'text.secondary' }}>3ème</Typography>
            {top3[2] ? (
              <>
                <Typography variant="h6">{top3[2].eleve.prenom} {top3[2].eleve.nom}</Typography>
                <Typography>{top3[2].wins} victoires • {top3[2].losses} défaites • {top3[2].arbitres} arbitres</Typography>
              </>
            ) : <Typography sx={{ color: 'text.secondary' }}>—</Typography>}
          </Paper>
        </Grid>
      </Grid>

      <Box sx={{ mt: 3 }}>
        <Paper sx={{ p: 2, backgroundColor: custom.surfaceLight }}>
          <Typography variant="subtitle1" sx={{ mb: 1 }}>Classement complet</Typography>
          <Table size="small">
            <TableHead>
              <TableRow sx={{ backgroundColor: custom.headerBg }}>
                <TableCell>Position</TableCell>
                <TableCell>Élève</TableCell>
                <TableCell align="right">Victoires</TableCell>
                <TableCell align="right">Défaites</TableCell>
                <TableCell align="right">Arbitres</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {elevesStats.map((row, idx) => (
                <TableRow key={row.eleve._id || idx} hover sx={{ '&:hover': { bgcolor: custom.rowHover } }}>
                  <TableCell>{idx + 1}</TableCell>
                  <TableCell>{row.eleve.prenom} {row.eleve.nom}</TableCell>
                  <TableCell align="right">{row.wins}</TableCell>
                  <TableCell align="right">{row.losses}</TableCell>
                  <TableCell align="right">{row.arbitres}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Paper>
      </Box>
    </Box>
  );
}